import type { FunctionComponent, SVGProps } from 'react';
import {
  CheckmarkIcon,
  WizCreateOrgIcon,
  WizBoundaryIcon,
  WizOrgHierarchyIcon,
  WizOrgEcMapIcon,
  WizAdminHierarchyIcon,
  WizAdminEcMapIcon,
} from '../assets/icons';

type IconType = FunctionComponent<SVGProps<SVGSVGElement>>;

interface WizardStep {
  title: string;
  Icon: IconType;
  iconClass: string;
}

/** The six wizard steps (icons from Figma, normalized to currentColor). */
const STEPS: WizardStep[] = [
  { title: 'Create Organization', Icon: WizCreateOrgIcon, iconClass: 'size-6' },
  { title: 'Define Organization Boundary', Icon: WizBoundaryIcon, iconClass: 'size-6' },
  { title: 'Build Party Hierarchy', Icon: WizOrgHierarchyIcon, iconClass: 'size-6' },
  { title: 'Party Level to EC Mapping', Icon: WizOrgEcMapIcon, iconClass: 'size-6' },
  { title: 'Build Administrative Hierarchy', Icon: WizAdminHierarchyIcon, iconClass: 'size-6' },
  { title: 'Admin Level to EC Mapping', Icon: WizAdminEcMapIcon, iconClass: 'size-6' },
];

const ACTIVE_RING =
  'shadow-[0px_0px_0px_4px_rgba(37,99,235,0.2),0px_4px_6px_-1px_rgba(0,74,198,0.2),0px_2px_4px_-2px_rgba(0,74,198,0.2)]';

interface OrgWizardStepperProps {
  /** Index (0-based) of the step currently in progress. */
  activeStep?: number;
  /** When provided, steps become clickable (e.g. to go back and edit). */
  onStepClick?: (index: number) => void;
  /** How many steps to render (defaults to all). Use to hide later steps. */
  visibleSteps?: number;
  /** Hide the "Pending" status text (e.g. in edit mode, where every step already has data). */
  hidePending?: boolean;
  /** Block clicking to steps ahead of the active one (e.g. during create — go back only). */
  disablePending?: boolean;
}

/** Shared wizard header. Scrolls horizontally on narrow screens. */
export default function OrgWizardStepper({
  activeStep = 0,
  onStepClick,
  visibleSteps,
  hidePending,
  disablePending,
}: OrgWizardStepperProps) {
  const steps = visibleSteps ? STEPS.slice(0, visibleSteps) : STEPS;
  return (
    <div className="overflow-x-auto pb-1">
      <div className="relative flex min-w-[960px] items-start justify-between gap-5 px-2 pt-1">
        {/* Connector line behind the circles — anchored to the first/last circle centers
            (px-2 padding + half of the 140px step width) so it connects every step. */}
        <div className="absolute left-[78px] right-[78px] top-7 h-0.5 rounded-full bg-line/30" />
        {steps.map(({ title, Icon, iconClass }, i) => {
          const active = i === activeStep;
          const completed = i < activeStep;
          const pending = i > activeStep;
          const clickable = Boolean(onStepClick) && !(disablePending && pending);
          return (
            <button
              key={title}
              type="button"
              onClick={() => onStepClick?.(i)}
              disabled={!clickable}
              className={[
                'relative z-[1] flex w-[140px] flex-col items-center',
                pending ? 'opacity-70' : '',
                clickable ? 'cursor-pointer' : 'cursor-default',
              ].join(' ')}
            >
              <div
                className={[
                  'flex size-14 items-center justify-center rounded-full',
                  active
                    ? `bg-brand text-white ${ACTIVE_RING}`
                    : completed
                      ? 'bg-brand text-white'
                      : 'bg-[#d1e2ff] text-brand',
                ].join(' ')}
              >
                {completed ? <CheckmarkIcon className="size-7" /> : <Icon className={iconClass} />}
              </div>
              <div className="flex flex-col items-center gap-1 pt-4 text-center">
                <p
                  className={
                    active
                      ? 'text-[18px] font-medium leading-[22px] text-ink'
                      : completed
                        ? 'text-base font-medium leading-5 text-ink'
                        : 'text-base leading-5 text-ink-secondary'
                  }
                >
                  {title}
                </p>
                {!(pending && hidePending) && (
                  <p
                    className={[
                      'text-sm font-medium leading-5',
                      pending ? 'text-ink-secondary' : 'text-ink',
                    ].join(' ')}
                  >
                    {active ? 'In Progress' : completed ? 'Completed' : 'Pending'}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
